import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { seriesColor } from '../constants/chartPalette';
import {
  ChartAxisLabels,
  ChartDataPoint,
  ChartGrid,
  ChartLegend,
  ChartValueAxis,
  enterAt,
  formatValue,
  niceScale,
  seriesChartToPlot,
  WidgetPeriodChip,
} from '../shared';

/**
 * Floor for the plot area. The plot itself flexes to whatever height the slot
 * gives it — a chart in a tall widget should draw a tall chart, not the same
 * 96px strip with air underneath — and this is the height below which the
 * bars stop being comparable at a glance.
 */
const PLOT_MIN_PX = 96;

/**
 * Bars — comparison between groups or stages.
 *
 * Two arrangements, one component, because they are the same chart answering
 * two questions:
 *
 * - `stacked={false}` (default) draws the series **side by side** in each
 *   category. With one series that degenerates to a plain bar chart, which is
 *   the common case.
 * - `stacked` draws them **on top of each other**, so the bar's height is the
 *   total and its segments are the make-up.
 *
 * Bars are scaled against a **round ceiling above** the largest total, never
 * against a fixed one: clamping overshoot would flatten two different months
 * into one identical bar, and scaling to the peak itself leaves the axis
 * labelled in units of 35.5 (see `niceScale`). The tallest bar stopping short
 * of the top is also how a reader can tell the chart is not clipped.
 *
 * Each bar prints its value above it — `display.show_values`. A dashboard is
 * read from across a desk; a figure that only exists in a hover tooltip is a
 * figure most readers never see. With grouped series the label goes on the
 * group's total, because that is the bar whose height it describes.
 *
 * ## Reading the contract (CT-5641)
 *
 * `BAR_CHART` and `STACKED_BAR` publish `series_chart`, the same shape the
 * line family does — an x axis, the y axes, and series whose points are
 * `{x, y}` pairs. `seriesChartToPlot` converts it; its note covers why the
 * categories are the union of every `x` and where a series' colour comes from.
 *
 * A series the query folded into «others» is drawn like the rest but in the
 * neutral colour the contract gave it, and it keeps its own label, so a reader
 * can tell the tail from a category.
 *
 * @param {object} props
 * @param {object} props.data `series_chart` — see propTypes.
 * @param {boolean} [props.stacked=false] Stack the series instead of grouping them.
 * @param {'top'|'bottom'} [props.legendPosition='top'] `display.legend.position`.
 * @param {string} [props.accent] Overrides the first series' colour.
 * @param {string} [props.className]
 */
function BarChartWidget({ data, stacked = false, legendPosition = 'top', accent, className = '' }) {
  // The contract's shape, or the parallel arrays a pre-v2 caller passes.
  const plot = useMemo(() => {
    if (!data) return null;
    if (Array.isArray(data.series) && data.series.some((entry) => entry.points)) {
      return seriesChartToPlot(data, { accent });
    }
    if (!Array.isArray(data.series)) return null;
    return {
      categories: data.categories ?? [],
      series: data.series.map((entry, index) => ({
        key: entry.label ?? String(index),
        label: entry.label ?? '',
        values: entry.values ?? [],
        color: seriesColor(index, accent),
        isOthers: false,
      })),
      format: null,
    };
  }, [accent, data]);

  const categories = useMemo(() => plot?.categories ?? [], [plot]);
  const series = useMemo(() => plot?.series ?? [], [plot]);
  const valueLabel = data?.valueLabel ?? '';

  const totals = useMemo(
    () =>
      categories.map((_, index) =>
        // A gap is not a zero: it simply does not contribute to the bar.
        stacked
          ? series.reduce((sum, entry) => sum + (entry.values[index] ?? 0), 0)
          : Math.max(...series.map((entry) => entry.values[index] ?? 0), 0),
      ),
    [categories, series, stacked],
  );

  // A round ceiling, and the ticks that go with it. `ChartGrid` draws the same
  // four rows, so every label lands on a line.
  const scale = useMemo(() => niceScale(Math.max(...totals, 0)), [totals]);
  const axis = data?.y_axes?.[0] ?? null;

  if (!categories.length || !series.length) return null;

  const legend = series.map((entry) => ({ label: entry.label, color: entry.color }));

  return (
    // `h-full` + a flexing plot: the widget fills its slot and the plot takes
    // whatever the legend and axis labels do not.
    <div className={`flex h-full min-h-0 flex-col ${className}`.trim()}>
      {legendPosition === 'top' ? <ChartLegend items={legend} /> : null}
      {axis?.label ? (
        <span className="text-[10px] text-content-muted">{axis.label}</span>
      ) : null}

      {/*
        Two columns: the value axis sizes itself to its widest tick and the
        plot takes the rest. The x labels sit in the plot's column so they
        stay under the marks rather than under the gutter.
      */}
      <div
        className="grid min-h-0 flex-1 gap-x-1.5"
        style={{ gridTemplateColumns: 'auto minmax(0, 1fr)', gridTemplateRows: 'minmax(0, 1fr) auto' }}
      >
        <ChartValueAxis ticks={scale.ticks} format={axis?.format ?? plot?.format} />

        <div className="relative min-h-0" style={{ minHeight: PLOT_MIN_PX }}>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0"
          aria-hidden
        >
          <ChartGrid />
        </svg>

        <div className="absolute inset-0 flex items-end gap-2 px-0.5">
          {categories.map((category, index) => {
            const total = totals[index];
            const heightPct = scale.max > 0 ? (total / scale.max) * 100 : 0;
            const parts = series.map((entry) => entry.values[index] ?? 0);
            const shown = (value) => formatValue(value, plot?.format);
            const summary = stacked
              ? series.map((entry, i) => `${shown(parts[i])} ${entry.label.toLowerCase()}`).join(', ')
              : series.map((entry, i) => `${entry.label} ${shown(parts[i])}`).join(' · ');

            const enter = enterAt(index);
            /*
              `flex-col-reverse`, not `flex-col`.

              The first series is the base of the stack — that is the
              convention everywhere, it is what the contract's own example
              draws, and it is what the stacked area already does. In normal
              column order `series[0]` renders first and therefore *on top*,
              which silently inverts every stacked bar: the baseline category
              floats at the ceiling and the «others» tail becomes the floor.
            */
            const barClass = stacked
              ? 'flex h-full w-full flex-col-reverse overflow-hidden rounded-t-[3px]'
              : 'flex h-full w-full items-end gap-px';

            return (
              <div
                key={category}
                className="relative flex flex-1 items-stretch"
                style={{ height: `${heightPct}%` }}
              >
                <span className="absolute -top-3.5 left-0 right-0 text-center text-[10px] font-semibold leading-none text-content">
                  {shown(total)}
                </span>
                <ChartDataPoint
                  fill="flex h-full w-full"
                  label={`${category} · ${shown(total)} ${valueLabel} — ${summary}`.trim()}
                >
                  <div className={`${barClass} ${enter.className}`} style={enter.style}>
                    {parts.map((part, partIndex) => (
                      <div
                        key={series[partIndex].key}
                        className={stacked ? '' : 'flex-1 rounded-t-[3px]'}
                        style={
                          stacked
                            ? { flex: part, background: series[partIndex].color }
                            : {
                                height: total > 0 ? `${(part / total) * 100}%` : 0,
                                background: series[partIndex].color,
                              }
                        }
                      />
                    ))}
                  </div>
                </ChartDataPoint>
              </div>
            );
          })}
        </div>
        </div>

        {/* Row 2, column 2: under the plot, never under the axis gutter. */}
        <div style={{ gridColumn: 2 }}>
          <ChartAxisLabels labels={categories} />
        </div>
      </div>

      {legendPosition === 'bottom' ? <ChartLegend items={legend} /> : null}
      <WidgetPeriodChip period={data?.period} className="mt-1 self-start" />
    </div>
  );
}

BarChartWidget.propTypes = {
  /** `series_chart`, or the pre-v2 parallel arrays. See `seriesChartToPlot`. */
  data: PropTypes.shape({
    x_axis: PropTypes.object,
    y_axes: PropTypes.arrayOf(PropTypes.object),
    series: PropTypes.arrayOf(PropTypes.object),
    period: PropTypes.object,
    // pre-v2
    categories: PropTypes.arrayOf(PropTypes.string),
    valueLabel: PropTypes.string,
  }).isRequired,
  stacked: PropTypes.bool,
  legendPosition: PropTypes.oneOf(['top', 'bottom']),
  accent: PropTypes.string,
  className: PropTypes.string,
};

export default memo(BarChartWidget);
